import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements, PaymentElement, useStripe, useElements,
} from '@stripe/react-stripe-js';
import {
  dataFetcher, baseUrl, getToken, STRIPE_PUBLIC_KEY,
} from 'Api';
import { MEMBERSHIP_PROMO_ENABLED } from 'Utils/settingsAccess';
import styles from './index.module.css';

// Load Stripe once. Null if no publishable key is configured for this env.
const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;
// Chosen plan is stashed here before a redirect so the result screen can
// render after Stripe sends the user back (React state is lost on reload).
const PENDING_KEY = 'pendingMembership';

const formatMoney = (cents) => `$${Math.round((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
// Current page URL without the query string — Stripe return/success target.
const pageBase = () => `${window.location.origin}${window.location.pathname}`;

// ---- Method B: embedded Payment Element form ----
// Handles both a PaymentIntent (amount due now) and a SetupIntent (isSetup — $0
// due now but we still collect + save the card so future renewals can charge it).
const CardForm = ({ payLabel, returnUrl, isSetup }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [cardHolder, setCardHolder] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    const name = cardHolder.trim();
    if (!name) { setErr('Please enter the card holder name.'); return; }
    setSubmitting(true);
    setErr('');
    // 3DS is handled automatically inside confirm*. On success Stripe redirects
    // to return_url; reaching here means it failed — stay and let them retry.
    // Card networks don't require a name for cards, so the Payment Element won't
    // render one (`name: 'never'` below). We collect it ourselves and attach it
    // to the payment method's billing_details here.
    const opts = {
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: { billing_details: { name } },
      },
    };
    const { error } = isSetup
      ? await stripe.confirmSetup(opts)
      : await stripe.confirmPayment(opts);
    setErr(error?.message || 'Could not be completed.');
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className={styles.coForm}>
      {/* Custom "Card holder name" field, styled to match the Stripe fields.
          It sits above the card block because the card/expiry/CVC/country all
          live inside Stripe's secure iframe — a custom field can't be placed
          between them. */}
      <div className={styles.coField}>
        <label className={styles.coLabel} htmlFor="co-card-holder">Card holder name</label>
        <input
          id="co-card-holder"
          className={styles.coInput}
          type="text"
          placeholder="e.g. Jane Cooper"
          value={cardHolder}
          onChange={(ev) => setCardHolder(ev.target.value)}
          autoComplete="cc-name"
        />
      </div>
      <div className={styles.coPeWrap}>
        <PaymentElement options={{ fields: { billingDetails: { name: 'never' } } }} />
      </div>
      {err && <p className={styles.coError}>{err}</p>}
      <button type="submit" className={styles.coPayBtn} disabled={!stripe || submitting}>
        {submitting ? 'Processing…' : payLabel}
      </button>
    </form>
  );
};

// Payment step for a chosen membership plan price. Three demo methods:
//   A) hosted Stripe Checkout (redirect)
//   B) embedded Payment Element (new card)
//   C) saved card (subscribe with an existing pm, 3DS on-page if needed)
// The backend order-status endpoint is the source of truth for every outcome.
const MembershipCheckout = ({
  plan, pricing, resume, canceled, onBack, onSuccess,
}) => {
  const [method, setMethod] = useState('card'); // 'checkout' (disabled) | 'card' | 'saved'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [intent, setIntent] = useState(null); // { client_secret, subscription_id }
  const [savedCards, setSavedCards] = useState(null); // null = not loaded yet
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedPm, setSelectedPm] = useState(null); // chosen payment_method_id
  const [rehydrated, setRehydrated] = useState(null); // plan recovered after redirect
  const [promoCode, setPromoCode] = useState('');
  const [promoResult, setPromoResult] = useState(null); // { code, valid, amount_after_cents, duration }
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMsg, setPromoMsg] = useState('');
  // select | confirming | done | failed | processing | canceled
  const [phase, setPhase] = useState(() => {
    if (canceled) return 'canceled';
    return resume ? 'confirming' : 'select';
  });
  const pollRef = useRef(0);

  // Fall back to the persisted plan when we've returned from a full-page redirect.
  const activePlan = plan || rehydrated?.plan;
  const activePricing = pricing || rehydrated?.pricing;
  // Promo (if applied & valid) discounts the amount we display and charge.
  const appliedPromo = promoResult?.valid ? promoResult.code : undefined;
  const effectiveCents = promoResult?.valid ? promoResult.amount_after_cents : activePricing?.price_cents;
  const amountLabel = activePricing ? `${formatMoney(effectiveCents)} / ${activePricing.billing_title}` : '';

  // ---- Shared: confirm order status (source of truth), polling if pending ----
  const confirmStatus = (query) => {
    setPhase('confirming');
    setError('');
    pollRef.current = 0;
    const poll = async () => {
      try {
        const { data: body } = await axios.get(`${baseUrl}/v4/membership/order-status`, {
          params: { ...query, token: getToken() },
        });
        const status = body?.data?.status;
        if (status === 'active') { setPhase('done'); return; }
        if (status === 'failed') {
          setError(body?.message || 'Payment failed — you were not charged.');
          setPhase('failed');
          return;
        }
        // pending: retry ~5× every 2s (a webhook/renewal may lag a moment)
        if (pollRef.current < 5) {
          pollRef.current += 1;
          setTimeout(poll, 2000);
        } else {
          setPhase('processing');
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Could not confirm your payment. Please try again.');
        setPhase('failed');
      }
    };
    poll();
  };

  useEffect(() => {
    // Recover the chosen plan for the result/retry screens after a redirect.
    if (!plan && (resume || canceled)) {
      try {
        const saved = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
        if (saved) setRehydrated(saved);
      } catch (e) { /* ignore */ }
    }
    if (resume) {
      confirmStatus(
        resume.sessionId ? { session_id: resume.sessionId } : { subscription_id: resume.subscriptionId },
      );
    }
  }, []);

  const persistPending = () => {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({ plan: activePlan, pricing: activePricing }));
    } catch (e) { /* ignore */ }
  };

  // ---- Promo code (shared by all methods) ----
  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code || !activePricing) return;
    setPromoLoading(true);
    setPromoMsg('');
    try {
      const body = await dataFetcher('v4/membership/validate-promo', {
        code,
        membership_plan_price_id: activePricing.id,
      });
      // Tolerate both a flat body and a { data: {...} } envelope.
      const d = body && typeof body.valid !== 'undefined' ? body : (body?.data || {});
      if (d.valid) {
        setPromoResult({ code, ...d });
        setPromoMsg('');
      } else {
        setPromoResult(null);
        setPromoMsg(body?.message || 'This promo code is invalid or expired.');
      }
    } catch (e) {
      setPromoResult(null);
      setPromoMsg(e?.response?.data?.message || 'Could not validate that code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setPromoResult(null);
    setPromoMsg('');
    setPromoCode('');
  };

  // ---- Method A: hosted checkout session, then redirect (disabled for now) ----
  // eslint-disable-next-line no-unused-vars
  const payWithCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      persistPending();
      const res = await dataFetcher('v4/membership/checkout-session', {
        membership_plan_price_id: activePricing.id,
        success_url: `${pageBase()}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${pageBase()}?checkout=cancelled`,
        ...(appliedPromo && { promo_code: appliedPromo }),
      });
      if (res?.success && res?.data?.url) {
        window.location = res.data.url;
      } else {
        setError(res?.message || 'Could not start checkout. Please try again.');
        setLoading(false);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  // ---- Method B: create a subscription intent, then mount PaymentElement ----
  const fetchIntent = async () => {
    setLoading(true);
    setError('');
    try {
      persistPending();
      const res = await dataFetcher('v4/membership/subscription-intent', {
        membership_plan_price_id: activePricing.id,
        ...(appliedPromo && { promo_code: appliedPromo }),
      });
      if (res?.success && res?.data?.client_secret) {
        setIntent(res.data);
      } else if (res?.success && res?.data?.subscription_id) {
        // No client_secret → the promo fully covers the first term ($0 due today),
        // so Stripe created no PaymentIntent. Keep the subscription so the user can
        // confirm — do NOT treat this as an error (that blocked free checkouts).
        setIntent(res.data);
      } else {
        setError(res?.message || 'Could not initialize payment.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not initialize payment.');
    } finally {
      setLoading(false);
    }
  };

  // (Re)create the subscription intent for the new-card path whenever the tab is
  // opened OR the applied promo changes — otherwise the PaymentElement would keep
  // confirming a stale full-price intent created before the promo was applied
  // (over-charging the customer). appliedPromo is in the deps for exactly this.
  useEffect(() => {
    if (method !== 'card' || phase !== 'select') return;
    setIntent(null); // drop any stale intent so the amount always reflects the promo
    fetchIntent();
  }, [method, appliedPromo]);

  // ---- Method C: saved card ----
  const fetchSavedCards = async () => {
    setSavedLoading(true);
    setError('');
    try {
      const { data: body } = await axios.get(`${baseUrl}/v4/membership/payment-methods`, {
        params: { token: getToken() },
      });
      const list = Array.isArray(body?.data) ? body.data : [];
      setSavedCards(list);
      const def = list.find((c) => c.is_default) || list[0];
      setSelectedPm(def ? (def.payment_method_id ?? def.id) : null);
      if (!body?.success && list.length === 0) setError(body?.message || 'Could not load your saved cards.');
    } catch (e) {
      setSavedCards([]);
      setError(e?.response?.data?.message || 'Could not load your saved cards.');
    } finally {
      setSavedLoading(false);
    }
  };

  // Load saved cards up front so we can hide the option when there are none.
  useEffect(() => {
    if (phase === 'select' && savedCards === null) fetchSavedCards();
  }, []);
  const hasSavedCards = Array.isArray(savedCards) && savedCards.length > 0;

  const payWithSavedCard = async () => {
    if (!selectedPm) return;
    setLoading(true);
    setError('');
    try {
      persistPending();
      const res = await dataFetcher('v4/membership/subscribe-saved-card', {
        membership_plan_price_id: activePricing.id,
        payment_method_id: selectedPm,
        ...(appliedPromo && { promo_code: appliedPromo }),
      });
      if (!res?.success || !res?.data) {
        setError(res?.message || 'Could not start the subscription.');
        setLoading(false);
        return;
      }
      const { status, client_secret: clientSecret, subscription_id: subId } = res.data;

      if (status === 'requires_action') {
        const stripe = await stripePromise;
        if (!stripe) { setError('Payments are not configured.'); setLoading(false); return; }
        const { error: authErr } = await stripe.handleNextAction({ clientSecret });
        if (authErr) { setError(authErr.message || 'Card authentication failed.'); setLoading(false); return; }
        confirmStatus({ subscription_id: subId });
        return;
      }
      if (status === 'active' || status === 'pending') {
        // Never final on its own — confirm/refresh via order-status.
        confirmStatus({ subscription_id: subId });
        return;
      }
      setError('Payment failed — please try another card.');
      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not complete the payment. Please try again.');
      setLoading(false);
    }
  };

  const finish = () => {
    let info = activePlan && activePricing ? { plan: activePlan, pricing: activePricing } : null;
    if (!info) {
      try { info = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null'); } catch (e) { info = null; }
    }
    try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
    onSuccess?.(info);
  };

  const retry = () => {
    setError('');
    setIntent(null);
    if (activePricing) setPhase('select');
    else onBack?.();
  };

  // ---- Result screens ----
  if (phase === 'confirming') {
    return (
      <div className={styles.coWrap}>
        <div className={styles.coState}>
          <div className={styles.coSpinner} />
          <p className={styles.coStateText}>Confirming your payment…</p>
          <p className={styles.coStateSub}>Checking with Stripe — this can take a few seconds.</p>
        </div>
      </div>
    );
  }
  if (phase === 'done') {
    return (
      <div className={styles.coWrap}>
        <div className={styles.coState}>
          <div className={styles.coCheck}>✓</div>
          <p className={styles.coStateText}>Payment successful</p>
          <p className={styles.coStateSub}>
            Your membership is active
            {activePricing ? ` — it renews automatically every ${activePricing.billing_title} on the same card` : ''}
            . No further action needed.
          </p>
          <button type="button" className={styles.coPayBtn} onClick={finish}>Continue</button>
        </div>
      </div>
    );
  }
  if (phase === 'processing') {
    return (
      <div className={styles.coWrap}>
        <div className={styles.coState}>
          <div className={styles.coInfo}>⋯</div>
          <p className={styles.coStateText}>Payment is processing</p>
          <p className={styles.coStateSub}>
            This is taking a little longer than usual. We&apos;ll confirm by email as soon as it&apos;s finalized.
          </p>
          <button type="button" className={styles.coSecondary} onClick={onBack}>Back to plans</button>
        </div>
      </div>
    );
  }
  if (phase === 'failed') {
    return (
      <div className={styles.coWrap}>
        <div className={styles.coState}>
          <div className={styles.coCross}>!</div>
          <p className={styles.coStateText}>Payment not completed</p>
          <p className={styles.coStateSub}>{error}</p>
          <button type="button" className={styles.coPayBtn} onClick={retry}>Try again</button>
          <button type="button" className={styles.coSecondary} onClick={onBack}>Back to plans</button>
        </div>
      </div>
    );
  }
  if (phase === 'canceled') {
    return (
      <div className={styles.coWrap}>
        <div className={styles.coState}>
          <div className={styles.coNeutral}>×</div>
          <p className={styles.coStateText}>Payment canceled</p>
          <p className={styles.coStateSub}>No charge was made. You can pick a plan again whenever you&apos;re ready.</p>
          <button type="button" className={styles.coPayBtn} onClick={onBack}>Back to plans</button>
        </div>
      </div>
    );
  }

  // ---- Method selection + pay ----
  return (
    <div className={styles.coWrap}>
      <button type="button" className={styles.coBack} onClick={onBack}>← Back to plans</button>

      <div className={styles.coSummary}>
        <span className={styles.coSummaryLabel}>You selected</span>
        <div className={styles.coSummaryRow}>
          <span className={styles.coPlanName}>{activePlan?.name}</span>
          <span className={styles.coAmount}>
            {promoResult?.valid && activePricing && (
              <span className={styles.coAmountWas}>{formatMoney(activePricing.price_cents)}</span>
            )}
            {amountLabel}
          </span>
        </div>
      </div>

      {MEMBERSHIP_PROMO_ENABLED && (
        <>
          <div className={styles.coPromo}>
            <input
              type="text"
              className={styles.coPromoInput}
              placeholder="Promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              disabled={promoResult?.valid || promoLoading}
            />
            {promoResult?.valid ? (
              <button type="button" className={styles.coPromoBtn} onClick={clearPromo}>Remove</button>
            ) : (
              <button
                type="button"
                className={styles.coPromoBtn}
                onClick={applyPromo}
                disabled={promoLoading || !promoCode.trim()}
              >
                {promoLoading ? 'Applying…' : 'Apply'}
              </button>
            )}
          </div>
          {promoMsg && <p className={styles.coPromoErr}>{promoMsg}</p>}
          {promoResult?.valid && (
            <p className={styles.coPromoOk}>
              Promo applied — you pay
              {' '}
              {formatMoney(promoResult.amount_after_cents)}
              {promoResult.duration === 'once' ? ' (first payment only)' : ''}
            </p>
          )}
        </>
      )}

      <div className={styles.coMethods}>
        {/* Hosted checkout disabled for now — only new/saved card.
        <button
          type="button"
          className={`${styles.coMethodTab} ${method === 'checkout' ? styles.coMethodActive : ''}`}
          onClick={() => setMethod('checkout')}
        >
          Hosted checkout
        </button>
        */}
        <button
          type="button"
          className={`${styles.coMethodTab} ${method === 'card' ? styles.coMethodActive : ''}`}
          onClick={() => setMethod('card')}
        >
          New card
        </button>
        {hasSavedCards && (
          <button
            type="button"
            className={`${styles.coMethodTab} ${method === 'saved' ? styles.coMethodActive : ''}`}
            onClick={() => setMethod('saved')}
          >
            Saved card
          </button>
        )}
      </div>

      {error && <p className={styles.coError}>{error}</p>}

      {/* Hosted checkout disabled for now.
      {method === 'checkout' && (
        <div className={styles.coCheckoutBody}>
          <p className={styles.coHint}>
            You&apos;ll be sent to Stripe&apos;s secure checkout, then returned here to confirm.
          </p>
          <button type="button" className={styles.coPayBtn} onClick={payWithCheckout} disabled={loading}>
            {loading ? 'Starting…' : `Continue to Checkout — ${amountLabel}`}
          </button>
        </div>
      )} */}

      {method === 'card' && (
        <div className={styles.coBody}>
          {loading && !intent && <div className={styles.coSpinner} />}
          {intent && intent.client_secret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret: intent.client_secret }}>
              <CardForm
                isSetup={intent.client_secret.startsWith('seti_')}
                payLabel={intent.client_secret.startsWith('seti_')
                  ? 'Save card & subscribe'
                  : `Pay ${amountLabel}`}
                returnUrl={`${pageBase()}?subscription_id=${intent.subscription_id}`}
              />
            </Elements>
          )}
          {intent && !intent.client_secret && (
            <>
              <p className={styles.coHint}>
                No payment due today — your promo covers the first term. You won&apos;t be charged now.
              </p>
              <button
                type="button"
                className={styles.coPayBtn}
                onClick={() => confirmStatus({ subscription_id: intent.subscription_id })}
                disabled={loading}
              >
                Confirm subscription
              </button>
            </>
          )}
          {!stripePromise && <p className={styles.coError}>Payments are not configured for this environment.</p>}
        </div>
      )}

      {method === 'saved' && (
        <div className={styles.coSavedBody}>
          {savedLoading && <div className={styles.coSpinner} />}
          {!savedLoading && savedCards && savedCards.length === 0 && (
            <p className={styles.coHint}>No saved cards on file. Use &quot;New card&quot; to enter one.</p>
          )}
          {savedCards && savedCards.length > 0 && (
            <>
              <div className={styles.coCards}>
                {savedCards.map((c) => {
                  const pmId = c.payment_method_id ?? c.id;
                  const exp = c.exp_month && c.exp_year
                    ? `${c.exp_month}/${String(c.exp_year).slice(-2)}`
                    : (c.exp || '');
                  return (
                    <button
                      type="button"
                      key={pmId}
                      className={`${styles.coCardRow} ${pmId === selectedPm ? styles.coCardRowActive : ''}`}
                      onClick={() => setSelectedPm(pmId)}
                    >
                      <span className={styles.coCardRadio} />
                      <span className={styles.coCardBrand}>{c.brand || 'Card'}</span>
                      <span className={styles.coCardNo}>
                        ••••
                        {' '}
                        {c.last4 || '••••'}
                      </span>
                      <span className={styles.coCardRight}>
                        {c.is_default ? <span className={styles.coCardDefault}>Default</span> : null}
                        {exp && <span className={styles.coCardExp}>{exp}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className={styles.coPayBtn}
                onClick={payWithSavedCard}
                disabled={loading || !selectedPm}
              >
                {loading ? 'Processing…' : `Pay ${amountLabel}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MembershipCheckout;
